import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import type { DevelopmentDetailDocumentResponse } from '@/domain/models/development.js';
import type { ReasonModel } from '@/domain/models/reason.js';
import { FileUpload } from '@/presentation/components/index.js';
import { DocumentDownloadRow } from '@/presentation/pages/developments/document-download-row.js';
import { DocumentRejectDialog } from '@/presentation/pages/developments/validation/document-reject-dialog.js';
import { DocumentReviewCard } from '@/presentation/pages/developments/validation/document-review-card.js';
import type { DevelopmentStatus } from '@/shared/utils/format-status.js';

const DEV_DOC_TYPES = ['DOCUMENTACAO_TECNICA', 'EVIDENCIA_TESTES_DEV', 'DOCUMENTO_CUTOVER'] as const;

const BASE_DEV_DOC_TYPES = ['DOCUMENTACAO_TECNICA', 'EVIDENCIA_TESTES_DEV'] as const;

const DEV_DOC_LABEL_KEYS: Record<(typeof DEV_DOC_TYPES)[number], string> = {
    DOCUMENTACAO_TECNICA: 'documents.technicalDoc',
    EVIDENCIA_TESTES_DEV: 'documents.testEvidence',
    DOCUMENTO_CUTOVER: 'documents.cutoverDoc'
};

const STATUS_REVIEW_GATE: DevelopmentStatus = 'AGUARDANDO_REVISAO_DOCUMENTACAO';
const STATUS_CONSULTANT_VALIDATION: DevelopmentStatus = 'EM_VALIDACAO_CONSULTOR';
const STATUS_UC_VALIDATION: DevelopmentStatus = 'EM_VALIDACAO_USUARIO_CHAVE';

interface EvidenceListProps {
    evidences: DevelopmentDetailDocumentResponse[];
    canRemove: boolean;
    onRemove: (id: string) => void;
    translate: (key: string) => string;
}

function EvidenceList({ evidences, canRemove, onRemove, translate }: EvidenceListProps) {
    if (evidences.length === 0) {
        return (
            <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                {translate('validation.noEvidencesYet')}
            </Typography>
        );
    }
    return (
        <Stack spacing={1} sx={{ mb: 2 }}>
            {evidences.map((doc) => (
                <Chip
                    key={doc.id}
                    label={doc.fileName}
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                    onDelete={canRemove ? () => onRemove(doc.id) : undefined}
                />
            ))}
        </Stack>
    );
}

export type EvidenceTarget = { type: 'cf' | 'uc'; id: string };

export interface ValidationTabProps {
    status: DevelopmentStatus;
    isCf: boolean;
    isUc: boolean;
    isGpc: boolean;
    ucAssigned: boolean;
    cutoverNotRequired: boolean;
    devDocuments: DevelopmentDetailDocumentResponse[];
    cfEvidences: DevelopmentDetailDocumentResponse[];
    ucEvidences: DevelopmentDetailDocumentResponse[];
    reviewReasons: ReasonModel[];
    submitting: boolean;
    onApproveDocumentation: () => void;
    onRejectDocumentation: (reasonId: string, notes: string) => void;
    onUploadEvidence: (file: File, type: 'cf' | 'uc') => void;
    onRemoveEvidence: (target: EvidenceTarget) => void;
    onDownloadDocument: (docId: string) => void;
    onReleaseToUc: () => void;
    onCloseWithoutUc: () => void;
    onApproveUc: () => void;
    translate: (key: string) => string;
}

export function ValidationTab({
    status,
    isCf,
    isUc,
    isGpc,
    ucAssigned,
    cutoverNotRequired,
    devDocuments,
    cfEvidences,
    ucEvidences,
    reviewReasons,
    submitting,
    onApproveDocumentation,
    onRejectDocumentation,
    onUploadEvidence,
    onRemoveEvidence,
    onDownloadDocument,
    onReleaseToUc,
    onCloseWithoutUc,
    onApproveUc,
    translate
}: ValidationTabProps) {
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

    const showDevDocuments = status === STATUS_REVIEW_GATE || status === STATUS_CONSULTANT_VALIDATION;
    const visibleDevDocTypes = cutoverNotRequired ? BASE_DEV_DOC_TYPES : DEV_DOC_TYPES;
    const showReviewCard = status === STATUS_REVIEW_GATE && (isCf || isGpc);
    const showCfEvidenceSection = status !== STATUS_REVIEW_GATE;
    const showUcSection = status !== STATUS_REVIEW_GATE && status !== STATUS_CONSULTANT_VALIDATION;
    const canCfEdit = (isCf || isGpc) && status === STATUS_CONSULTANT_VALIDATION;
    // GPC só aprova/encerra aqui quando não há UC atribuído ao desenvolvimento; havendo UC,
    // só o Usuário Chave designado pode aprovar (regra D1, validada no backend em uc-approve.ts).
    const canApproveUc = (isUc || (isGpc && !ucAssigned)) && status === STATUS_UC_VALIDATION;

    return (
        <Stack spacing={2}>
            {showDevDocuments && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            {translate('documents.mandatoryDocs')}
                        </Typography>
                        <Stack spacing={1}>
                            {visibleDevDocTypes.map((docType) => {
                                const doc = devDocuments.find((d) => d.docType === docType);
                                return doc ? (
                                    <DocumentDownloadRow
                                        key={docType}
                                        label={`${translate(DEV_DOC_LABEL_KEYS[docType])}: ${doc.fileName}`}
                                        uploadedAt={doc.uploadedAt}
                                        downloadAriaLabel={`${translate('documents.download')}: ${doc.fileName}`}
                                        onDownload={() => onDownloadDocument(doc.id)}
                                    />
                                ) : (
                                    <Typography key={docType} variant="body2" color="text.disabled">
                                        {translate(DEV_DOC_LABEL_KEYS[docType])}: {translate('documents.noDocUploaded')}
                                    </Typography>
                                );
                            })}
                        </Stack>
                    </CardContent>
                </Card>
            )}
            {showReviewCard && (
                <DocumentReviewCard
                    approving={submitting}
                    rejecting={submitting}
                    onApprove={onApproveDocumentation}
                    onRequestReject={() => setRejectDialogOpen(true)}
                    translate={translate}
                />
            )}
            {showCfEvidenceSection && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                            {translate('validation.consultorValidation')}
                        </Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                            {translate('validation.cfTestEvidences')}
                        </Typography>
                        <EvidenceList evidences={cfEvidences} canRemove={canCfEdit} onRemove={(docId) => onRemoveEvidence({ type: 'cf', id: docId })} translate={translate} />
                        {canCfEdit && <FileUpload onFileSelected={(file) => onUploadEvidence(file, 'cf')} label={translate('validation.uploadEvidence')} uploading={submitting} />}
                        {canCfEdit && cfEvidences.length === 0 && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                                {translate('validation.evidenceRequiredToRelease')}
                            </Typography>
                        )}
                        {canCfEdit && (
                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                <Button size="small" variant="outlined" onClick={onReleaseToUc} disabled={submitting || cfEvidences.length === 0}>
                                    {translate('validation.releaseToUc')}
                                </Button>
                                <Button size="small" variant="outlined" onClick={onCloseWithoutUc} disabled={submitting || cfEvidences.length === 0}>
                                    {translate('validation.approveAndClose')}
                                </Button>
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}
            {showUcSection && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                            {translate('validation.keyUserValidation')}
                        </Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                            {translate('validation.ucTestEvidences')}
                        </Typography>
                        <EvidenceList evidences={ucEvidences} canRemove={canApproveUc} onRemove={(docId) => onRemoveEvidence({ type: 'uc', id: docId })} translate={translate} />
                        {canApproveUc && (
                            <FileUpload onFileSelected={(file) => onUploadEvidence(file, 'uc')} label={translate('validation.uploadEvidence')} uploading={submitting} />
                        )}
                        {canApproveUc && ucEvidences.length === 0 && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                                {translate('validation.evidenceRequiredToClose')}
                            </Typography>
                        )}
                        {canApproveUc && (
                            <Button size="small" variant="contained" sx={{ color: 'white', mt: 2 }} onClick={onApproveUc} disabled={submitting || ucEvidences.length === 0}>
                                {translate('validation.approveAndClose')}
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}
            <DocumentRejectDialog
                open={rejectDialogOpen}
                reasons={reviewReasons}
                submitting={submitting}
                onClose={() => setRejectDialogOpen(false)}
                onConfirm={(reasonId, notes) => {
                    setRejectDialogOpen(false);
                    onRejectDocumentation(reasonId, notes);
                }}
                translate={translate}
            />
        </Stack>
    );
}
